import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import AutoScrollCarousel from '../components/AutoScrollCarousel';
import InteractiveMap from '../components/InteractiveMap';
import SEO from '../components/SEO';
import { useTranslation } from '../contexts/LanguageContext';
import { useMarket } from '../contexts/MarketContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Building2, Compass, Users, ChevronRight, ChevronLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const { t } = useTranslation();
  const { market, marketLink } = useMarket();
  const [properties, setProperties] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchData();
  }, [market]);

  const fetchData = async () => {
    try {
      const [propRes, settingsRes, aboutRes] = await Promise.all([
        axios.get(`${API}/properties`, { params: { market } }),
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/about-page`)
      ]);
      setProperties(propRes.data);
      setSiteSettings(settingsRes.data);

      // ✅ Партньорите идват от about-page API (същото като /about страницата)
      const apiPartners = aboutRes.data?.partners || [];
      setPartners(apiPartners);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const slides = siteSettings?.slides?.length > 0 ? siteSettings.slides : [
    {
      image: 'https://static.prod-images.emergentagent.com/jobs/b735739e-c886-4c93-8a6e-3153729431d7/images/f16f1a716efc198502344241b66ccabbcc1519ccd44d63255171aa492a326137.png',
      title: '',
      subtitle: '',
      cta_text: '',
      cta_link: '/properties'
    }
  ];

  useEffect(() => {
    if (slides.length <= 1) return;
    if (siteSettings?.slideshow_autoplay === false) return;
    const speed = (siteSettings?.slideshow_speed || 6) * 1000;
    let order = [...Array(slides.length).keys()];
    if (siteSettings?.slideshow_order === 'random') {
      order = order.sort(() => Math.random() - 0.5);
    }
    const timer = setInterval(() => {
      setCurrentSlide((prev) => {
        const currentIdx = order.indexOf(prev);
        const nextIdx = (currentIdx + 1) % order.length;
        return order[nextIdx];
      });
    }, speed);
    return () => clearInterval(timer);
  }, [slides.length, siteSettings?.slideshow_autoplay, siteSettings?.slideshow_speed, siteSettings?.slideshow_order]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const services = [
    { icon: Compass, titleKey: 'home.svc_design', descKey: 'home.svc_design_desc' },
    { icon: Building2, titleKey: 'home.svc_construction', descKey: 'home.svc_construction_desc' },
    { icon: Users, titleKey: 'home.svc_individual', descKey: 'home.svc_individual_desc' }
  ];

  const currentSlideData = slides[currentSlide] || slides[0];

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <SEO
        title={t('seo.home_title')}
        description={t('seo.home_desc')}
        url="/"
      />
      <Header />

      <section className="hero-section relative" data-testid="hero-section">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`hero-bg absolute inset-0 ${
              siteSettings?.slideshow_transition === 'slide'
                ? `transition-transform duration-700 ${index === currentSlide ? 'translate-x-0' : index < currentSlide ? '-translate-x-full' : 'translate-x-full'}`
                : `transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`
            }`}
            style={{ backgroundImage: `url(${slide.image})` }}
          />
        ))}
        <div className="hero-overlay" />
        <div className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 relative z-10">
          <span className="text-sm uppercase tracking-[0.2em] text-white/80 mb-4 block animate-fade-in-up">
            {t('home.build_dream')}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight leading-tight transition-all duration-500">
            {currentSlideData.title || t('home.projects_title')}
          </h1>
          {/* Hidden H1 for SEO crawlers - static content */}
          <h1 className="sr-only">{t('seo.home_h1')}</h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-xl mb-8 leading-relaxed transition-all duration-500">
            {currentSlideData.subtitle || t('home.projects_subtitle')}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to={marketLink(currentSlideData.cta_link || '/properties')}>
              <Button size="lg" className="bg-white text-[#1B5E37] hover:bg-white/90" data-testid="hero-cta-projects">
                {currentSlideData.cta_text || t('home.view_all_projects')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to={marketLink('/contact')}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="hero-cta-contact">
                {t('home.contact_us')}
              </Button>
            </Link>
          </div>
        </div>

        {slides.length > 1 && (
          <>
            <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors" aria-label="Previous slide">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors" aria-label="Next slide">
              <ChevronRight className="w-6 h-6" />
            </button>
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {slides.map((_, index) => (
                <button key={index} onClick={() => setCurrentSlide(index)} className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/70'}`} aria-label={`Slide ${index + 1}`} />
              ))}
            </div>
          </>
        )}
      </section>

      <section className="py-24 bg-accent" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('home.our_services')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('home.services_short')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-xl border border-border hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 bg-[#1B5E37]/10 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#1B5E37]" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-foreground mb-4">{t(service.titleKey)}</h3>
                <p className="text-muted-foreground leading-relaxed">{t(service.descKey)}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services">
              <Button variant="outline">
                {t('home.view_all_services')}
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-2">{t('home.projects_title')}</h2>
              <p className="text-lg text-muted-foreground">{t('home.projects_subtitle')}</p>
            </div>
            <Link to="/properties">
              <Button variant="outline" data-testid="view-all-projects-btn">
                {t('home.view_all')}
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner"></div></div>
          ) : (
            <AutoScrollCarousel items={properties} />
          )}
        </div>
      </section>

      <section className="py-24 bg-secondary" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('home.project_locations')}</h2>
            <p className="text-lg text-muted-foreground">{t('home.locations_subtitle')}</p>
          </div>
          <div className="h-[500px] rounded-xl overflow-hidden border border-border">
            <InteractiveMap properties={properties} />
          </div>
        </div>
      </section>

      {/* ✅ Партньори — зареждат се от API (About page), синхронизирани с admin панела */}
      {partners.length > 0 && (
        <section className="py-24" data-testid="partners-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">{t('home.partners_title')}</h2>
              <p className="text-lg text-muted-foreground">{t('home.partners_subtitle')}</p>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {partners.map((partner, index) => (
                <a
                  key={partner.id || index}
                  href={partner.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-32 h-20 flex items-center justify-center grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all"
                  title={partner.name}
                >
                  {partner.logo ? (
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<span class="text-sm font-medium text-muted-foreground">${partner.name}</span>`;
                      }}
                    />
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground">{partner.name}</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-24 bg-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">{t('home.cta_title')}</h2>
          <p className="text-xl text-white/80 mb-8">{t('home.cta_subtitle')}</p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-primary hover:bg-white/90" data-testid="cta-contact-btn">
              {t('home.cta_contact')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
